import React, { useEffect, useRef, useState } from 'react';
import { ELEMENTS, NATIVE_APPS, appMark } from '@ofself/shell';
import { ElementCell } from './Shelf';
import { AppGlyph, PersonFace } from './faces';
import { API_BASE } from '../../services/apiConfig';

// The cells every Nucleus page shares: an app as its logo, a person as a face,
// a realm as rings (design.oS, "Faces").

// An approved app's row (/app-shell/apps) or a catalog entry, as the fleet's
// registry knows it: its hue and its logo when it is one of ours.
export function elementOf(app) {
    const name = String(app?.name || '').toLowerCase();
    // the registry is one fleet: the elements and the native apps (apps.js)
    const el = [...ELEMENTS, ...NATIVE_APPS].find((e) => e.id === app?.id || e.id === name || String(e.name).toLowerCase() === name);
    return el || null;
}

// AN UPLOADED LOGO, IN ITS BRAND COLOUR. A third party's SVG logo is drawn in
// currentColor with no hue of its own (migration 268), so an <img> of it renders
// black: an image cannot inherit a colour. So an SVG is read once, cleaned, and
// drawn inline in the app's brand_color (the layer's hue when it has none); any
// other image stays an <img>, coloured as it was made.
const logos = new Map(); // logo url → Promise<{ svg } | { src }>
function readLogo(url) {
    if (!logos.has(url)) {
        logos.set(url, fetch(url).then(async (r) => {
            if (!r.ok) throw new Error(String(r.status));
            if (!/svg/i.test(r.headers.get('content-type') || '')) return { src: url };
            return { svg: cleanSvg(await r.text()) };
        }).catch(() => ({ src: url })));
    }
    return logos.get(url);
}
// THE CLIENT'S OWN GUARD, and an allowlist. The server refuses an unsafe SVG on
// upload and on serve, but by pattern, and this markup is inlined into Nucleus. A
// list of things to remove missed <animate> and <set>, which write any value into
// an href — a javascript: link — and a scheme split by a tab or an entity, so what
// survives is only what draws a logo. An element not named here goes with its
// children.
const SVG_DRAWING = new Set([
    'svg', 'g', 'defs', 'symbol', 'use', 'title', 'desc', 'style',
    'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
    'text', 'tspan', 'textpath', 'lineargradient', 'radialgradient', 'stop',
    'clippath', 'mask', 'pattern', 'marker',
    'filter', 'feblend', 'fecolormatrix', 'fecomponenttransfer', 'fecomposite',
    'feconvolvematrix', 'fediffuselighting', 'fedisplacementmap', 'fedistantlight',
    'fedropshadow', 'feflood', 'fefunca', 'fefuncb', 'fefuncg', 'fefuncr',
    'fegaussianblur', 'femerge', 'femergenode', 'femorphology', 'feoffset',
    'fepointlight', 'fespecularlighting', 'fespotlight', 'fetile', 'feturbulence',
]);
// CSS that could reach outside the logo: an escape (`@\69mport`, `url(\68ttps:`
// get past any pattern), an @-rule, or a url() that does not point inside the file.
const CSS_UNSAFE = /\\|@|url\((?!\s*['"]?\s*#)/i;
export function cleanSvg(text) {
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    const svg = doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== 'svg') return null;
    [...svg.querySelectorAll('*')].forEach((n) => {
        if (!SVG_DRAWING.has(n.localName.toLowerCase())) n.remove();
    });
    // Comments and processing instructions are serialized verbatim, and the HTML
    // parser reads them differently from XML: a PI ends at its first `>`, so what
    // follows it can leave the <svg> as a real element. Text and CDATA are escaped
    // or stay text.
    const walker = doc.createTreeWalker(svg, 0x80 | 0x40); // SHOW_COMMENT | SHOW_PROCESSING_INSTRUCTION
    const strays = [];
    while (walker.nextNode()) strays.push(walker.currentNode);
    strays.forEach((n) => n.remove());
    [svg, ...svg.querySelectorAll('*')].forEach((n) => [...n.attributes].forEach((a) => {
        // A reference may point inside the file (`#id`) and nowhere else.
        const local = (a.localName || a.name).toLowerCase();
        if (local.startsWith('on') || (local === 'href' && !/^#/.test(a.value.trim()))
                || (local === 'style' && CSS_UNSAFE.test(a.value))) {
            n.removeAttributeNode(a);
        }
    }));
    // A stylesheet is kept for the classes design tools style a logo with. It applies
    // only inside the logo's shadow root (UploadedLogo), but it can still make a
    // request, so one that could is dropped whole.
    [...svg.querySelectorAll('style')].forEach((n) => { if (CSS_UNSAFE.test(n.textContent)) n.remove(); });
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    return new XMLSerializer().serializeToString(svg);
}
// The logo in a shadow root of its own: a <style> inside an inline SVG otherwise
// applies to the whole page, so an app's logo could restyle Nucleus (hide or fake
// the consent controls). `color` still reaches it — it is inherited — so a logo
// drawn in currentColor takes the cell's colour.
const SHADOW_CSS = '<style>:host{display:inline-grid;place-items:center}svg{width:100%;height:100%;display:block}</style>';
export function ShadowSvg({ markup, style }) {
    const host = useRef(null);
    useEffect(() => {
        const el = host.current;
        if (!el) return;
        (el.shadowRoot || el.attachShadow({ mode: 'open' })).innerHTML = SHADOW_CSS + markup;
    }, [markup]);
    return <span ref={host} className="nx-logo" style={style} aria-hidden="true" />;
}

function UploadedLogo({ url, color, size }) {
    const [logo, setLogo] = useState(null);
    useEffect(() => {
        let live = true;
        readLogo(url).then((l) => { if (live) setLogo(l); });
        return () => { live = false; };
    }, [url]);
    const box = { width: size, height: size, display: 'inline-grid', placeItems: 'center' };
    if (logo?.svg) return <ShadowSvg markup={logo.svg} style={{ ...box, color }} />;
    if (logo?.src) return <img src={logo.src} alt="" width={size} height={size} style={{ objectFit: 'contain' }} />;
    return <span style={box} aria-hidden="true" />;
}

export function AppFace({ app, size = 50 }) {
    const el = elementOf(app);
    // an app shows its logo: the registry's mark for one of ours, the logo it
    // uploaded for a third party's (in its brand colour), else the app glyph
    const mark = el ? appMark(el, null) : null;
    if (mark) return mark;
    // read from the API's public logo address (any origin may read it), not the
    // signed storage link, which a script on this origin may not be let to fetch
    return app?.logo_url || app?.logo_file_id
        ? <UploadedLogo url={`${API_BASE}/third-party/${app.id}/logo`} color={app.brand_color || 'var(--nx-zone-hue, var(--ofself-ink))'} size={size} />
        : <AppGlyph size={size} hue={el?.hue} />;
}

export function AppCell({ app, note, badge, dim, current, onPress, ...rest }) {
    const el = elementOf(app);
    return (
        <ElementCell
            hue={el?.hue || '#8a8378'}
            mark={<AppFace app={app} />}
            name={app?.name}
            note={note}
            badge={badge}
            dim={dim}
            current={current}
            onPress={onPress}
            {...rest}
        />
    );
}

export function PersonCell({ person, dim, onPress, ...rest }) {
    return (
        <ElementCell
            tag={person.role && person.role !== 'member' ? person.role : undefined}
            mark={<PersonFace url={person.avatar || person.avatar_url} size={50} />}
            name={person.name}
            note={person.realm || person.note}
            dim={dim}
            onPress={onPress}
            {...rest}
        />
    );
}

export function RealmRings({ color = '#8fa376' }) {
    return (
        <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
            <circle cx="28" cy="28" r="26.5" fill="none" stroke={color} strokeOpacity=".35" />
            <circle cx="28" cy="28" r="17.5" fill="none" stroke={color} strokeOpacity=".6" />
            <circle cx="28" cy="28" r="8" fill={color} fillOpacity=".85" />
        </svg>
    );
}

export function RealmCell({ realm, reach = 0, dim, onPress, ...rest }) {
    const color = realm.color || '#8fa376';
    return (
        <ElementCell
            hue={color}
            tag={realm.is_default ? 'DEFAULT' : undefined}
            mark={<RealmRings color={color} />}
            name={realm.name}
            note={reach === 0 ? 'nobody reaches in' : `${reach} reach through`}
            dim={dim}
            onPress={onPress}
            title={realm.description || realm.name}
            {...rest}
        />
    );
}
